import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Auth({ mode: initialMode }) {
  const location = useLocation();
  const [mode, setMode] = useState(initialMode || (location.pathname === "/registrati" ? "register" : "login"));
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "register"
        ? await register(form.name, form.email, form.password)
        : await login(form.email, form.password);
      toast.success(mode === "register" ? "Benvenuto!" : "Accesso effettuato");
      if (user.role === "admin" || user.role === "manager") {
        navigate("/admin");
      } else if (mode === "register" || user.contract_version !== "v1.0") {
        navigate("/contratto");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A1128] text-white flex flex-col">
      <div className="px-5 sm:px-8 py-6"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-8">
          <h1 className="font-display text-2xl font-semibold">
            {mode === "register" ? "Crea il tuo account" : "Bentornato"}
          </h1>
          <p className="mt-2 text-sm text-white/60">
            {mode === "register" ? "Registrati per iniziare a segnalare." : "Accedi alla tua area personale."}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm text-white/70">Nome e cognome</label>
                <input data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg bg-[#0A1128]/60 border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Mario Rossi" />
              </div>
            )}
            <div>
              <label className="text-sm text-white/70">Email</label>
              <input data-testid="auth-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full rounded-lg bg-[#0A1128]/60 border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
                placeholder="tu@email.it" />
            </div>
            <div>
              <label className="text-sm text-white/70">Password</label>
              <input data-testid="auth-password-input" type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full rounded-lg bg-[#0A1128]/60 border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
                placeholder="••••••••" />
            </div>
            <button data-testid="auth-submit-btn" disabled={loading}
              className="btn-gold w-full rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "register" ? "Registrati" : "Accedi"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/15" />
            <span className="text-xs text-white/40">oppure</span>
            <span className="h-px flex-1 bg-white/15" />
          </div>
          <button type="button" onClick={googleLogin} data-testid="google-login-btn"
            className="w-full rounded-full bg-white text-slate-800 font-medium py-3 flex items-center justify-center gap-3 hover:bg-white/90 transition-colors">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continua con Google
          </button>
          <p className="mt-6 text-center text-sm text-white/60">
            {mode === "register" ? "Hai già un account?" : "Non hai un account?"}{" "}
            <button data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-[#D4AF37] font-medium hover:underline">
              {mode === "register" ? "Accedi" : "Registrati"}
            </button>
          </p>
          <p className="mt-4 text-center text-xs text-white/40">
            <Link to="/" className="hover:text-white/70">← Torna alla home</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
